import React, { createContext, useContext, useState, ReactNode } from 'react';

interface Language {
  code: string;
  name: string;
  flag: string;
}

interface LanguageContextType {
  currentLanguage: Language;
  availableLanguages: Language[];
  setLanguage: (language: Language) => void;
  translate: (key: string) => string;
}

const languages: Language[] = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'ro', name: 'Română', flag: '🇷🇴' }
];

const translations: Record<string, Record<string, string>> = {
  en: {
    welcome: 'Welcome to DVSA Training',
    login: 'Login',
    email: 'Email',
    password: 'Password',
    dashboard: 'Dashboard',
    training: 'Training',
    progress: 'Progress',
    admin: 'Administration',
    logout: 'Logout',
    startTraining: 'Start Training',
    continueTraining: 'Continue Training',
    completeHours: 'Complete Hours',
    requiredHours: 'Required Hours'
  },
  es: {
    welcome: 'Bienvenido al Entrenamiento DVSA',
    login: 'Iniciar Sesión',
    email: 'Correo Electrónico',
    password: 'Contraseña',
    dashboard: 'Panel de Control',
    training: 'Entrenamiento',
    progress: 'Progreso',
    admin: 'Administración',
    logout: 'Cerrar Sesión',
    startTraining: 'Comenzar Entrenamiento',
    continueTraining: 'Continuar Entrenamiento',
    completeHours: 'Horas Completadas',
    requiredHours: 'Horas Requeridas'
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

interface LanguageProviderProps {
  children: ReactNode;
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(languages[0]);

  const setLanguage = (language: Language) => {
    setCurrentLanguage(language);
  };

  const translate = (key: string): string => {
    return translations[currentLanguage.code]?.[key] || translations.en[key] || key;
  };

  const value = {
    currentLanguage,
    availableLanguages: languages,
    setLanguage,
    translate
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}